/**
 * Settings page — `?tab=account|billing`.
 *
 * signup-billing-mvp Phase 6 Session 1 (2026-05-13) introduced the tab strip
 * mirroring `/admin?tab=...`'s `adminSearchSchema` precedent. `account` is the
 * default tab (existing behavior preserved); `billing` mounts `<BillingTab>`
 * shell. Validate-search wiring lives in `App.tsx`'s `settingsRoute` config.
 */

import { z } from 'zod';
import clsx from 'clsx';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AccountSettings } from '@/components/settings';
import { BillingHub } from '@/components/billing';

export const SETTINGS_TABS = ['account', 'billing'] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export const settingsSearchSchema = z.object({
  tab: z.enum(SETTINGS_TABS).catch('account').default('account'),
});

interface SettingsTabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}

function SettingsTabButton({
  active,
  onClick,
  children,
  testid,
}: SettingsTabButtonProps) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={clsx(
        'px-4 py-1.5 text-sm font-mono tracking-[1px] cursor-pointer transition-colors border-b-2',
        active
          ? 'text-text-bright border-accent'
          : 'text-text-muted border-transparent hover:text-text'
      )}
    >
      {children}
    </button>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { tab } = useSearch({ from: '/settings' });

  return (
    <div className="p-4 max-w-2xl flex flex-col gap-3 overflow-y-auto h-full">
      <div className="flex items-center gap-1 border-b border-panel-border">
        <SettingsTabButton
          active={tab === 'account'}
          onClick={() =>
            navigate({ to: '/settings', search: { tab: 'account' } })
          }
          testid="settings-tab-account"
        >
          Account
        </SettingsTabButton>
        <SettingsTabButton
          active={tab === 'billing'}
          onClick={() =>
            navigate({ to: '/settings', search: { tab: 'billing' } })
          }
          testid="settings-tab-billing"
        >
          Billing
        </SettingsTabButton>
      </div>
      {tab === 'account' && <AccountSettings />}
      {tab === 'billing' && <BillingHub />}
    </div>
  );
}
